if (!customElements.get('ll-visual-store')) {
  customElements.define('ll-visual-store', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.scrollArea = this.querySelector('[data-vs-scroll]');
      this.rootScreen = this.querySelector('[data-vs-root]');
      this.panels = Array.from(this.querySelectorAll('[data-vs-panel]'));
      this.dialog = this.closest('dialog');
      this.positions = new Map();
      this.currentPanel = null;
      this.originLink = null;
      this.historyKey = 'lionVisualStoreReturn';
      this.menuEntryKey = 'lionVisualStoreMenuEntry';
      this.restoring = false;
      this.pageLeaving = false;
      this.pendingNavigation = false;
      this.pendingAnchorNavigation = false;
      this.restoredSnapshot = null;
      this.restoreSequence = 0;
      this.historyTransitionPending = false;
      this.historyClosing = false;
      this.pendingMenuClose = null;
      const signal = this.controller.signal;

      this.addEventListener('click', event => {
        if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
        this.cancelRestoration();
        const open = event.target.closest('[data-vs-open]');
        if (open) {
          const panel = this.panels.find(item => item.id === open.dataset.vsOpen);
          if (!panel) return;
          event.preventDefault();
          this.openPanel(panel, open);
          return;
        }
        if (event.target.closest('[data-vs-back]')) {
          this.backToRoot();
          return;
        }
        const link = event.target.closest('a[href]');
        if (!link || link.hasAttribute('download') || link.matches('[data-cart-drawer-trigger]')) return;
        if (link.target && link.target !== '_self') return;
        if (link.closest('.ll-vs__utility, .ll-vs__brand')) {
          this.clearReturnState();
          return;
        }
        const destination = new URL(link.href, window.location.href);
        if (destination.origin !== window.location.origin) return;
        if (destination.href === window.location.href) return;
        if (destination.pathname === window.location.pathname && destination.hash) {
          if (destination.hash === window.location.hash || !document.getElementById(destination.hash.slice(1))) return;
          this.pendingAnchorNavigation = true;
          this.writeReturnState(true, link.getAttribute('href'));
          return;
        }
        this.pendingNavigation = true;
        this.writeReturnState(true, link.getAttribute('href'));
      }, { signal });

      this.dialog?.addEventListener('cancel', event => {
        if (!event.cancelable || !this.dialog.open || !this.currentPanel || !window.matchMedia('(max-width: 1099px)').matches) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.cancelRestoration();
        this.backToRoot();
      }, { capture: true, signal });

      this.dialog?.addEventListener('close', () => {
        if (this.pageLeaving || this.pendingAnchorNavigation) return;
        if (this.dialog.open) return;
        window.clearTimeout(this.scrollSaveTimer);
        this.pendingNavigation = false;
        this.cancelRestoration();
        if (this.historyClosing) {
          this.historyClosing = false;
          return;
        }
        const entry = this.readMenuEntry();
        if (entry?.kind === 'panel') {
          this.writeReturnState(false);
          this.pendingMenuClose = { groupId: entry.groupId, parentEntryId: entry.parentEntryId };
          if (!this.historyTransitionPending) {
            this.historyTransitionPending = true;
            window.history.back();
          }
          return;
        }
        this.clearReturnState();
      }, { signal });

      this.observer = new MutationObserver(() => {
        if (!this.dialog.open || this.restoring) return;
        const key = this.panelKey(this.currentPanel);
        requestAnimationFrame(() => {
          if (this.dialog.open && !this.restoring) this.scrollArea.scrollTop = this.positions.get(key) || 0;
        });
      });
      if (this.dialog) this.observer.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      this.scrollArea.addEventListener('scroll', () => {
        if (!this.dialog?.open || this.restoring) return;
        this.positions.set(this.panelKey(this.currentPanel), this.scrollArea.scrollTop);
        window.clearTimeout(this.scrollSaveTimer);
        this.scrollSaveTimer = window.setTimeout(() => {
          if (this.dialog.open && this.readMenuEntry()) this.writeReturnState(true, this.readReturnState()?.focusHref);
        }, 120);
      }, { passive: true, signal });

      window.addEventListener('pagehide', () => {
        window.clearTimeout(this.scrollSaveTimer);
        if (!this.pendingNavigation && this.readReturnState()) this.writeReturnState(this.dialog.open);
        this.pageLeaving = true;
        this.restoredSnapshot = null;
      }, { signal });
      window.addEventListener('pageshow', event => {
        this.pageLeaving = false;
        this.pendingNavigation = false;
        this.pendingAnchorNavigation = false;
        this.restoreReturnState(event.persisted);
      }, { signal });
      window.addEventListener('hashchange', () => { this.pendingAnchorNavigation = false; }, { signal });
      window.addEventListener('popstate', () => {
        if (!window.matchMedia('(max-width: 1099px)').matches) return;
        this.cancelRestoration();
        window.clearTimeout(this.scrollSaveTimer);
        this.positions.set(this.panelKey(this.currentPanel), this.scrollArea.scrollTop);
        this.historyTransitionPending = false;
        this.pendingAnchorNavigation = false;
        this.restoredSnapshot = null;
        if (this.pendingMenuClose) {
          const entry = this.readMenuEntry();
          const expected = this.pendingMenuClose;
          this.pendingMenuClose = null;
          if (entry?.groupId === expected.groupId && entry.entryId === expected.parentEntryId) this.showRoot();
          this.writeReturnState(false);
          return;
        }
        if (this.readReturnState()?.open) {
          this.restoreReturnState(true);
        } else if (this.dialog.open) {
          this.historyClosing = true;
          const destination = window.location.href;
          const target = document.getElementById(window.location.hash.slice(1));
          this.dialog.addEventListener('close', () => {
            if (window.location.href === destination) target?.scrollIntoView({ block: 'start', behavior: 'instant' });
          }, { once: true, signal });
          if (window.LionTheme?.closeDialog) window.LionTheme.closeDialog(this.dialog, false);
          else this.dialog.close();
        }
      }, { signal });
      this.restoreReturnState(false);
    }

    backToRoot() {
      if (this.historyTransitionPending) return;
      if (this.readMenuEntry()?.kind === 'panel') {
        this.writeReturnState(true);
        this.historyTransitionPending = true;
        window.history.back();
      } else {
        this.showRoot();
        this.writeReturnState(true);
      }
    }

    panelKey(panel) {
      return panel?.querySelector('.ll-vs__all')?.getAttribute('href') || 'root';
    }

    token() {
      return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    readMenuEntry() {
      const entry = window.history.state?.[this.menuEntryKey];
      if (entry?.version !== 1 || entry.source !== window.location.href || !entry.groupId || !entry.entryId) return null;
      return entry.kind === 'root' && entry.depth === 0 || entry.kind === 'panel' && entry.depth === 1 && entry.parentEntryId ? entry : null;
    }

    openPanel(panel, originLink) {
      if (this.historyTransitionPending) return;
      this.positions.set('root', this.scrollArea.scrollTop);
      this.originLink = originLink;
      if (this.writeReturnState(true, originLink.getAttribute('href'))) {
        const existing = window.history.state;
        let rootEntry = this.readMenuEntry();
        if (rootEntry?.kind !== 'root') rootEntry = { version: 1, source: window.location.href, groupId: this.token(), entryId: this.token(), kind: 'root', depth: 0 };
        const rootState = Object.assign(Array.isArray(existing) ? [] : {}, existing, { [this.menuEntryKey]: rootEntry });
        const panelState = Object.assign(Array.isArray(existing) ? [] : {}, rootState, {
          [this.menuEntryKey]: { ...rootEntry, entryId: this.token(), parentEntryId: rootEntry.entryId, kind: 'panel', depth: 1 },
          [this.historyKey]: { ...rootState[this.historyKey], panel: this.panelKey(panel), focusHref: null, token: this.token() }
        });
        try {
          window.history.replaceState(rootState, '');
          window.history.pushState(panelState, '');
        } catch (_) {}
      }
      this.showPanel(panel);
      this.writeReturnState(true);
    }

    cancelRestoration() {
      this.restoreSequence += 1;
      this.restoring = false;
      this.dialog.removeAttribute('data-vs-restoring');
    }

    readReturnState() {
      const saved = window.history.state?.[this.historyKey];
      return saved?.version === 1 && saved.source === window.location.href ? saved : null;
    }

    writeReturnState(open, focusHref = null) {
      if (this.restoring) return false;
      const existingState = window.history.state;
      if (existingState !== null && typeof existingState !== 'object') return false;
      this.positions.set(this.panelKey(this.currentPanel), this.scrollArea.scrollTop);
      const saved = {
        version: 1,
        source: window.location.href,
        open,
        panel: this.panelKey(this.currentPanel),
        positions: Object.fromEntries(this.positions),
        focusHref,
        token: this.token()
      };
      const state = Object.assign(Array.isArray(existingState) ? [] : {}, existingState, { [this.historyKey]: saved });
      try {
        window.history.replaceState(state, '');
        return true;
      } catch (_) { return false; }
    }

    clearReturnState() {
      if (!this.readReturnState()) return;
      this.writeReturnState(false);
    }

    restoreReturnState(persisted) {
      const navigation = window.performance?.getEntriesByType('navigation')[0];
      if (!persisted && navigation?.type !== 'back_forward') return;
      if (!window.matchMedia('(max-width: 1099px)').matches) return;
      const saved = this.readReturnState();
      if (!saved?.open || this.restoring || this.restoredSnapshot === saved.token) return;
      this.restoredSnapshot = saved.token;
      this.restoring = true;
      this.historyClosing = false;
      const sequence = ++this.restoreSequence;
      this.positions = new Map([...Object.entries(saved.positions || {}).map(([key, position]) => [key, Math.max(0, Number(position) || 0)]), ...this.positions]);
      const panel = this.panels.find(item => this.panelKey(item) === saved.panel) || null;
      this.panels.forEach(item => { item.hidden = item !== panel; });
      this.rootScreen.hidden = Boolean(panel);
      this.currentPanel = panel;
      this.originLink = panel ? this.querySelector(`[data-vs-open="${panel.id}"]`) : null;
      this.querySelectorAll('[data-vs-open]').forEach(link => link.setAttribute('aria-expanded', String(link === this.originLink)));
      const trigger = this.closest('ll-header')?.querySelector('[data-mobile-menu-open]');
      this.dialog.setAttribute('data-vs-restoring', '');
      try {
        if (window.LionTheme?.openDialog) window.LionTheme.openDialog(this.dialog, trigger);
        else if (!this.dialog.open) this.dialog.showModal();
      } catch (_) {
        this.dialog.removeAttribute('data-vs-restoring');
        this.restoring = false;
        return;
      }
      this.scrollArea.scrollTop = this.positions.get(this.panelKey(panel)) || 0;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (sequence !== this.restoreSequence) return;
        if (this.dialog.open) {
          this.scrollArea.scrollTop = this.positions.get(this.panelKey(panel)) || 0;
          const screen = panel || this.rootScreen;
          const focusLink = Array.from(screen.querySelectorAll('a[href]')).find(link => link.getAttribute('href') === saved.focusHref);
          focusLink?.focus({ preventScroll: true });
        }
        this.dialog.removeAttribute('data-vs-restoring');
        this.restoring = false;
      }));
    }

    showPanel(panel) {
      this.panels.forEach(item => { item.hidden = item !== panel; });
      this.rootScreen.hidden = true;
      this.currentPanel = panel;
      this.originLink?.setAttribute('aria-expanded', 'true');
      this.scrollArea.scrollTop = this.positions.get(this.panelKey(panel)) || 0;
      panel.querySelector('[data-vs-back]')?.focus({ preventScroll: true });
    }

    showRoot() {
      if (this.currentPanel) this.positions.set(this.panelKey(this.currentPanel), this.scrollArea.scrollTop);
      this.panels.forEach(item => { item.hidden = true; });
      this.rootScreen.hidden = false;
      this.currentPanel = null;
      this.scrollArea.scrollTop = this.positions.get('root') || 0;
      this.originLink?.setAttribute('aria-expanded', 'false');
      this.originLink?.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      window.clearTimeout(this.scrollSaveTimer);
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
    }
  });
}



